import React, { useEffect, useState, useCallback } from "react";
import { PieChart, Pie, Cell, Tooltip } from "recharts";

const LegendInput = ({ label, value, onChange, editable = true, color }) => {
  const [inputVal, setInputVal] = useState(value);

  useEffect(() => {
    setInputVal(value); // update if external value changes
  }, [value]);

  const handleChange = (e) => {
    const raw = e.target.value.replace(/[^0-9.]/g, "");
    setInputVal(raw);

    if (raw === "") return;
    const parsed = parseFloat(raw);
    if (!isNaN(parsed)) {
      onChange(parsed);
    }
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "start",
          marginBottom: "5px",
          padding: "8px 12px",
          // borderRadius: "8px",
          backgroundColor: "#f9f9f9",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          fontFamily: "Arial, sans-serif",
          fontSize: "14px",
          width: "100%",
          borderBottom: "2px solid lightgray",
        }}
      >
        <div
          style={{
            width: 16,
            height: 16,
            backgroundColor: color,
            marginRight: 12,
            borderRadius: 4,
          }}
        />
        <span style={{ flex: 1, fontWeight: "500", color: "#333" }}>
          {label}
        </span>
        {editable ? (
          <input
            type="text"
            value={`$ ${Number(inputVal).toLocaleString()}`}
            onChange={handleChange}
            style={{
              width: "90px",
              padding: "5px",
              textAlign: "start",
              marginLeft: "7px",
              borderRadius: "6px",
              border: "1px solid #ccc",
              fontSize: "14px",
              outline: "none",
              transition: "border-color 0.2s ease",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#007bff")}
            onBlur={(e) => (e.target.style.borderColor = "#ccc")}
          />
        ) : (
          <span
            style={{
              marginRight: 10,
              fontWeight: "bold",
              color: "#222",
              // fontSize: 16,
            }}
          >
            $ {Number(value).toLocaleString()}
          </span>
        )}
      </div>
    </>
  );
};

const MortgageCalculator = () => {
  const [homePrice, setHomePrice] = useState(500000);
  const [downPayment, setDownPayment] = useState(100000);
  const [loanTerm, setLoanTerm] = useState(360);
  const [interestRate, setInterestRate] = useState(6.5);
  const [propertyTax, setPropertyTax] = useState(0);
  const [insurance, setInsurance] = useState(0);
  const [hoaFees, setHoaFees] = useState(0);
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isSmallScreen = viewportWidth <= 500;

  const calculateEMI = useCallback(() => {
    const principal = homePrice - downPayment;
    const monthlyInterest = interestRate / 100 / 12;
    const numPayments = loanTerm;

    const monthlyEMI =
      monthlyInterest === 0
        ? principal / numPayments
        : (principal *
            monthlyInterest *
            Math.pow(1 + monthlyInterest, numPayments)) /
          (Math.pow(1 + monthlyInterest, numPayments) - 1);

    const estimatedPayment =
      monthlyEMI + propertyTax / 12 + insurance + hoaFees;
    setMonthlyPayment(estimatedPayment.toFixed(2));
  }, [
    homePrice,
    downPayment,
    loanTerm,
    interestRate,
    propertyTax,
    insurance,
    hoaFees,
  ]);

  useEffect(() => {
    calculateEMI();
  }, [calculateEMI]);

  const handleMonthlyPaymentChange = (newMonthlyPayment) => {
    setMonthlyPayment(newMonthlyPayment);
    const monthlyInterest = interestRate / 100 / 12;
    const numPayments = loanTerm;
    const adjustedMonthlyPayment =
      newMonthlyPayment - propertyTax / 12 - insurance - hoaFees;

    const newPrincipal =
      monthlyInterest === 0
        ? adjustedMonthlyPayment * numPayments
        : (adjustedMonthlyPayment *
            (Math.pow(1 + monthlyInterest, numPayments) - 1)) /
          (monthlyInterest * Math.pow(1 + monthlyInterest, numPayments));

    const newHomePrice = newPrincipal + downPayment;
    setHomePrice(newHomePrice.toFixed(0));
  };

  const principalAndInterest =
    parseFloat(monthlyPayment) - propertyTax / 12 - insurance - hoaFees;

  const data = [
    { name: "Principal & Interest", value: principalAndInterest },
    { name: "Property Tax", value: propertyTax / 12 },
    { name: "Homeowner's Insurance", value: insurance },
    { name: "HOA Fees", value: hoaFees },
  ];

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];
  const hoverBackgroundColor = ["#005bb5", "#008a7f", "#cc9600", "#cc5f29"];

  return (
    <div className="mortgageCalculatorParent">
      <div className="mortgageCalculator">
        <div className="mortgageCalculatorHeaderContent">
          <span className="mortgageCalculatorHeader">
            Monthly Payment Calculator for Mortgages*
          </span>
          <span className="mortgageCalculatorContent">
            Use this calculator to determine the monthly payments for different
            loan amounts.
          </span>
        </div>

        <div
          className="mortgageCalculatorBox"
          style={{ display: "flex", flexWrap: "wrap" }}
        >
          <div className="leftSideBox">
            <label>
              Home Price: <br />
              <input
                type="text"
                min="0"
                value={`$ ${Number(homePrice).toLocaleString()}`}
                onChange={(e) =>
                  setHomePrice(+e.target.value.replace(/[^0-9.-]+/g, ""))
                }
              />
              <input
                type="range"
                min="0"
                max="2000000"
                step="1"
                value={homePrice}
                onChange={(e) => setHomePrice(+e.target.value)}
                style={{ width: "100%" }}
              />
            </label>
            <label>
              Down Payment: <br />
              <span>
                <input
                  type="text"
                  min="0"
                  value={`$ ${Number(downPayment).toLocaleString()}`}
                  onChange={(e) =>
                    setDownPayment(+e.target.value.replace(/[^0-9.-]+/g, ""))
                  }
                />
              </span>
            </label>

            <label>
              Length of Loan: <br />
              <select
                value={loanTerm}
                onChange={(e) => setLoanTerm(+e.target.value)}
              >
                {Array.from({ length: 29 }, (_, i) => (i + 2) * 12).map(
                  (term) => (
                    <option key={term} value={term}>
                      {term} months (or) {term / 12} years
                    </option>
                  )
                )}
              </select>
              <input
                type="range"
                min="24"
                max="360"
                step="12"
                value={loanTerm}
                onChange={(e) => setLoanTerm(+e.target.value)}
                style={{ width: "100%" }}
              />
            </label>

            <label>
              Interest Rate (%): <br />
              <div style={{ display: "flex", alignItems: "center" }}>
                <input
                  type="text"
                  min="0"
                  max="100"
                  step="0.001"
                  value={interestRate}
                  onChange={(e) => {
                    let value = e.target.value.replace(/[^0-9.]/g, "");

                    // Allow only one decimal point
                    if ((value.match(/\./g) || []).length > 1) {
                      value = value.slice(0, -1);
                    }

                    // Ensure there are no more than two digits after the decimal point
                    const [integer, decimal] = value.split(".");
                    if (decimal && decimal.length > 2) {
                      value = `${integer}.${decimal.slice(0, 3)}`; // Limit to two decimal places
                    }

                    // Ensure the value is between 0 and 100
                    if (+value < 0) value = "0";
                    if (+value > 100) value = "100";

                    setInterestRate(value);
                  }}
                  style={{ width: "100%" }} // Adjust width as needed
                />
                <span style={{ marginLeft: "-30px", fontWeight: "bold" }}>
                  %
                </span>
              </div>
            </label>
            <b
              style={{
                color: "blue",
                cursor: "pointer",
                marginTop: "10px",
                display: "inline-block",
                textAlign: "center",
              }}
              onClick={() => setShowAdvanced((prev) => !prev)}
            >
              {showAdvanced ? "Hide advanced options" : "Show advanced options"}
            </b>

            {showAdvanced && (
              <>
                <label>
                  Property Tax: <br />
                  <input
                    type="text"
                    min="0"
                    value={`$ ${Number(
                      Math.round(propertyTax / 12)
                    ).toLocaleString()}`}
                    onChange={(e) => {
                      const monthlyTax = +e.target.value.replace(
                        /[^0-9.-]+/g,
                        ""
                      );
                      setPropertyTax(monthlyTax * 12);
                    }}
                  />
                </label>

                <label>
                  Homeowner's Insurance: <br />
                  <input
                    type="text"
                    min="0"
                    value={`$ ${Number(insurance).toLocaleString()}`}
                    onChange={(e) =>
                      setInsurance(+e.target.value.replace(/[^0-9.-]+/g, ""))
                    }
                  />
                </label>

                <label>
                  HOA Fees: <br />
                  <input
                    type="text"
                    min="0"
                    value={`$ ${Number(hoaFees).toLocaleString()}`}
                    onChange={(e) =>
                      setHoaFees(+e.target.value.replace(/[^0-9.-]+/g, ""))
                    }
                  />
                </label>
              </>
            )}

            <div className="RACrightSideBottom00">
              <div>
                <span style={{ color: "red" }}>
                  * PMI usually applies when your downpayment or equity in the
                  home is under 20%.
                </span>
              </div>
            </div>
          </div>

          <div className="rightSideBox">
            <p className="rightSideHeader">
              <span className="rightSideHeaderPrice">
                $ {Number(Math.round(monthlyPayment)).toLocaleString()}
              </span>
              <span> Your estimated monthly payment</span>
            </p>

            <input
              type="range"
              min="300"
              max="9000"
              step="1"
              value={monthlyPayment}
              onChange={(e) => handleMonthlyPaymentChange(+e.target.value)}
              style={{ width: "100%" }}
            />

            <div className="rightSideBoxPieChartLegendInputBox">
              <div
                style={{
                  position: "relative",
                  textAlign: "center",
                }}
              >
                <PieChart
                  width={isSmallScreen ? 250 : 250}
                  height={isSmallScreen ? 250 : 250}
                >
                  <Pie
                    data={data}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={isSmallScreen ? 80 : 80}
                    outerRadius={isSmallScreen ? 120 : 120}
                    paddingAngle={1}
                  >
                    {data.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          hoveredIndex === index
                            ? hoverBackgroundColor[index % COLORS.length]
                            : COLORS[index % COLORS.length]
                        }
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(null)}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [
                      `$ ${Number(Math.round(value)).toLocaleString()}`,
                      name,
                    ]}
                  />
                </PieChart>

                <div
                  className="rightSideHeaderPrice"
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    fontWeight: "bold",
                  }}
                >
                  $ {Number(Math.round(monthlyPayment)).toLocaleString()}
                </div>
              </div>

              <div>
                <div
                  style={{
                    marginBottom: "15px",
                    fontWeight: "bold",
                    fontSize: 15,
                  }}
                >
                  How is my monthly payment calculated?
                </div>
                <LegendInput
                  label="Principal & Interest"
                  value={principalAndInterest.toFixed()}
                  editable={false}
                  color={COLORS[0]}
                />
                <LegendInput
                  label="Property Tax"
                  value={(propertyTax / 12).toFixed()}
                  onChange={(val) => setPropertyTax(+val * 12)}
                  color={COLORS[1]}
                />
                <LegendInput
                  label="Homeowner's Insurance"
                  value={insurance.toFixed()}
                  onChange={(val) => setInsurance(+val)}
                  color={COLORS[2]}
                />
                <LegendInput
                  label="HOA Fees"
                  value={hoaFees.toFixed()}
                  onChange={(val) => setHoaFees(+val)}
                  color={COLORS[3]}
                />
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginTop: "20px",
                    fontWeight: "bold",
                    // fontSize: 17,
                  }}
                >
                  <span>Total monthly payment =</span> ${" "}
                  {Number(Math.round(monthlyPayment)).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MortgageCalculator;
